import {
  useGetAllocationPlanQuery,
  useGetAllocationPerformanceQuery,
  useGetGridStatusQuery,
  useGetSettingsQuery,
  useGetStatusQuery,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import { fmtUsd } from "@/lib/format";

/**
 * SystemPictureCard — one-stop view of the entire trading stack.
 *
 * Answers three questions:
 *   1. WHERE is my money?  (free USDT + per-asset holdings + per-sleeve allocation)
 *   2. WHAT is each engine doing right now?  (on/off, live/paper, recent activity)
 *   3. HOW is each engine performing?  (last 30d P&L per sleeve)
 *
 * Data sources (all already polling-cached by RTK Query):
 *   - /api/status                  → balances + free quote
 *   - /api/allocation/plan         → sleeve $ split + per-engine caps
 *   - /api/allocation/performance  → realized P&L per sleeve, last 30d
 *   - /api/grid/status             → grid regime + active run
 *   - /api/settings                → engine enable/pause flags
 */
export function SystemPictureCard() {
  const status = useGetStatusQuery(undefined, { pollingInterval: 30000 });
  const plan = useGetAllocationPlanQuery(undefined, { pollingInterval: 60000 });
  const perf = useGetAllocationPerformanceQuery(30, { pollingInterval: 60000 });
  const grid = useGetGridStatusQuery(undefined, { pollingInterval: 30000 });
  const settings = useGetSettingsQuery();

  const p: any = plan.data || {};
  const sleeves: any[] = p.sleeves || [];
  const caps: any = p.engine_caps || {};
  const perfBySleeve: any = perf.data?.sleeves || {};
  const totalRealized = Number(perf.data?.total_realized_pnl_usd || 0);
  const gridS: any = grid.data || {};
  const settingsData: any = settings.data || {};

  // Engine status table — one row per major engine
  const engineRows = [
    {
      key: "pre_pump_auto",
      label: "Pre-pump auto",
      sleeve: "Aggressive",
      enabled: settingsData.pre_pump_auto_trader_enabled,
      cfg_link: "/admin/pre-pump",
      size_usd: caps.pre_pump_auto?.position_usd,
      mode: "live",
    },
    {
      key: "alpha_bsc_auto",
      label: "Alpha BSC auto",
      sleeve: "Aggressive",
      enabled: settingsData.alpha_bsc_auto_enabled,
      cfg_link: "/alpha-bsc/auto",
      size_usd: caps.alpha_bsc_auto?.per_trade_usd,
      mode: "live (BSC)",
    },
    {
      key: "conviction_dca",
      label: "Conviction DCA",
      sleeve: "Conviction",
      enabled: settingsData.conviction_dca_enabled,
      cfg_link: "/admin/dca",
      size_usd: caps.conviction_dca?.per_fill_usd,
      mode: "live",
    },
    {
      key: "grid_trader",
      label: "Grid trader",
      sleeve: "Ballast",
      enabled: settingsData.grid_trader_enabled,
      cfg_link: "/grid",
      size_usd: gridS.paper_equity_usdt,
      mode: gridS.live_enabled ? "LIVE" : "paper",
      extra: gridS.symbol && (
        <span className="font-mono text-xs text-muted ml-1">· {gridS.symbol}</span>
      ),
    },
    {
      key: "dca_engine",
      label: "Standard DCA",
      sleeve: "—",
      enabled: settingsData.dca_engine_enabled,
      cfg_link: "/admin/dca",
      size_usd: Number(settingsData.dca_amount_usd_per_run || 0),
      mode: "live",
      extra: <span className="text-xs text-muted ml-1">every {settingsData.dca_interval_hours || "?"}h</span>,
    },
    {
      key: "scalp",
      label: "Scalp",
      sleeve: "—",
      enabled: settingsData.scalp_enabled && !settingsData.scalp_paused,
      cfg_link: "/scalp/settings",
      size_usd: Number(settingsData.scalp_per_trade_usd || 0),
      mode: settingsData.scalp_paused ? "paused" : "live",
    },
    {
      key: "hourly",
      label: "Hourly cycle",
      sleeve: "—",
      enabled: !settingsData.paused,
      cfg_link: "/cycles",
      size_usd: Number(settingsData.max_per_trade_usd || 0),
      mode: settingsData.paused ? "paused" : (settingsData.dry_run ? "dry-run" : "live"),
    },
  ];

  // Portfolio summary
  const positions = (status.data?.open_positions_usd ?? {}) as Record<string, number>;
  const freeQuote = Number(status.data?.free_quote ?? 0);
  const positionsTotal = Object.values(positions).reduce((a, b) => a + Number(b || 0), 0);
  const grand = freeQuote + positionsTotal;
  const allocTotal = Number(p.total_capital_usdt || 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          System picture
          <span className="text-xs text-muted ml-2 font-normal">
            full stack snapshot — where the money is, what's running, what each piece has done
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* 1. PORTFOLIO TOTALS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Portfolio total" value={fmtUsd(grand)} sub={`${Object.keys(positions).length} assets`} />
          <Tile label="Free USDT" value={fmtUsd(freeQuote)} sub="ready to deploy"
                tone={freeQuote < 50 ? "warn" : "good"} />
          <Tile label="Deployed in positions" value={fmtUsd(positionsTotal)}
                sub={grand > 0 ? `${((positionsTotal / grand) * 100).toFixed(0)}% of port` : ""} />
          <Tile label="30d realized P&L"
                value={`${totalRealized >= 0 ? "+" : ""}${fmtUsd(totalRealized)}`}
                sub="across all engines"
                tone={totalRealized > 0 ? "good" : totalRealized < 0 ? "bad" : "muted"} />
        </div>

        {/* 2. ALLOCATION SLEEVES */}
        <div>
          <div className="text-xs text-muted uppercase tracking-wide mb-2">
            Allocation policy <Link to="/allocation" className="text-fg/80 hover:underline">→ configure</Link>
            <span className="ml-2">(target ${allocTotal.toLocaleString()})</span>
          </div>
          {plan.isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {sleeves.map((s: any) => {
                const sleevePnl = (s.engines || []).reduce(
                  (acc: number, e: string) => acc + Number(perfBySleeve[e]?.realized_pnl || 0), 0);
                const sleeveTrades = (s.engines || []).reduce(
                  (acc: number, e: string) => acc + Number(perfBySleeve[e]?.trades || 0), 0);
                return (
                  <div key={s.name} className="border border-border bg-bg2 rounded p-2">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium capitalize">{s.name}</div>
                      <Badge tone="muted">{(s.pct * 100).toFixed(0)}%</Badge>
                    </div>
                    <div className="text-xl font-semibold tabular mt-0.5">{fmtUsd(s.usd)}</div>
                    <div className="text-xs text-muted">
                      {sleeveTrades} trades · 30d:{" "}
                      <span className={sleevePnl > 0 ? "text-emerald-400" : sleevePnl < 0 ? "text-rose-400" : ""}>
                        {sleevePnl >= 0 ? "+" : ""}{fmtUsd(sleevePnl)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 3. ENGINE ALLOCATION TABLE — sizing + 30d performance only.
            Live on/off + activity status moved to EngineHealthStrip on
            the Overview page, so this table no longer duplicates it. */}
        <div>
          <div className="text-xs text-muted uppercase tracking-wide mb-2">
            Engine allocation · 30d performance
            <span className="ml-2 normal-case">(live status → engine health strip)</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className="py-1 pr-3">Engine</th>
                  <th className="py-1 pr-3">Sleeve</th>
                  <th className="py-1 pr-3 text-right">Per-trade size</th>
                  <th className="py-1 pr-3 text-right">30d trades</th>
                  <th className="py-1 pr-3 text-right">30d P&L</th>
                  <th className="py-1 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {engineRows.map((e) => {
                  const ePerf = perfBySleeve[e.key] || { trades: 0, realized_pnl: 0 };
                  return (
                    <tr key={e.key} className="border-t border-border/40">
                      <td className="py-1.5 pr-3 font-medium">
                        {e.label}{e.extra || null}
                      </td>
                      <td className="py-1.5 pr-3 text-xs text-muted">{e.sleeve}</td>
                      <td className="py-1.5 pr-3 text-right tabular text-xs">
                        {e.size_usd ? fmtUsd(e.size_usd) : "—"}
                      </td>
                      <td className="py-1.5 pr-3 text-right tabular">{ePerf.trades}</td>
                      <td className={`py-1.5 pr-3 text-right tabular ${ePerf.realized_pnl > 0 ? "text-emerald-400" : ePerf.realized_pnl < 0 ? "text-rose-400" : ""}`}>
                        {ePerf.realized_pnl !== 0 ? (ePerf.realized_pnl >= 0 ? "+" : "") + fmtUsd(ePerf.realized_pnl) : "—"}
                      </td>
                      <td className="py-1.5 pr-3">
                        <Link to={e.cfg_link} className="text-xs text-muted hover:text-fg">→</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* 4. GRID TRADER PER-SYMBOL SNAPSHOT */}
        {gridS.enabled && gridS.by_symbol && (
          <div className="border border-border bg-bg2 rounded p-3">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="text-sm font-medium">
                Grid trader · {Object.keys(gridS.by_symbol).length} symbols ·
                <span className="text-muted ml-1">
                  ${gridS.per_symbol_equity}/symbol
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Badge tone={gridS.aggregated_realized_pnl_usd > 0 ? "good"
                              : gridS.aggregated_realized_pnl_usd < 0 ? "bad" : "muted"}>
                  {gridS.aggregated_fills_count ?? 0} fills · ${(gridS.aggregated_realized_pnl_usd ?? 0).toFixed(2)} realized
                </Badge>
                <Link to="/grid" className="text-xs text-muted hover:text-fg">→ detail</Link>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(gridS.by_symbol).map(([sym, info]: [string, any]) => {
                const r = info.regime || {};
                const verdict = r.verdict || "?";
                return (
                  <div key={sym} className="border border-border bg-bg rounded p-2">
                    <div className="flex items-center justify-between gap-1">
                      <span className="font-mono text-xs font-semibold">{sym}</span>
                      <Badge tone={verdict === "grid_friendly" ? "good" : verdict === "disable" ? "bad" : "warn"}>
                        {r.score ?? "?"}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted mt-0.5">
                      ${Number(r.current_price ?? 0).toLocaleString(undefined,{maximumFractionDigits:2})}
                      {info.id ? ` · run #${info.id}` : ""}
                    </div>
                    <div className="text-xs text-muted">
                      {info.fills_summary?.n ?? 0} fills · {" "}
                      <span className={(info.fills_summary?.realized_pnl ?? 0) > 0 ? "text-emerald-400" : (info.fills_summary?.realized_pnl ?? 0) < 0 ? "text-rose-400" : ""}>
                        ${(info.fills_summary?.realized_pnl ?? 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  const cls = tone === "good" ? "border-emerald-500/30"
    : tone === "bad" ? "border-rose-500/30"
    : tone === "warn" ? "border-amber-500/30" : "border-border";
  return (
    <div className={`rounded border ${cls} bg-bg2 p-3`}>
      <div className="text-xs text-muted uppercase tracking-wide">{label}</div>
      <div className="text-xl font-semibold tabular">{value}</div>
      {sub && <div className="text-xs text-muted">{sub}</div>}
    </div>
  );
}
